import React, { useState, useEffect } from "react";
import { useSnackbar } from "./context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import {
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    Delete,
    Edit,
    FilterAlt,
    Launch,
    MapOutlined,
    MoreVert,
    PlaylistAdd,
    PlaylistRemove,
    Remove,
    SearchOff,
} from "@mui/icons-material";
import { project_api_path } from "./api_registry";

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PARAM_KEYS = ["state", "district", "block"];

const EMPTY_NEW = {
    name: "",
    description: "",
    default_layers: true,
    additional_params: { state: "", district: "", block: "" },
};

const EMPTY_FILTERS = { state: "", district: "", block: "" };

// ─── Additional Params Editor ─────────────────────────────────────────────────
// Styled to match the screenshot: blue header, Key / Value columns,
// green "Add" button for custom rows, red "Remove" button for removable rows.
// Default keys (state, district, block) are always present and cannot be removed.

function AdditionalParamsEditor({ params, onChange, disabled }) {
    const toRows = (obj) =>
        Object.entries(obj ?? {}).map(([key, value]) => ({ key, value }));

    const toObj = (rows) =>
        Object.fromEntries(rows.map(({ key, value }) => [key, value]));

    const [rows, setRows] = useState(() => {
        const existing = toRows(params ?? {});
        const existingKeys = existing.map((r) => r.key);
        const defaults = DEFAULT_PARAM_KEYS.filter(
            (k) => !existingKeys.includes(k)
        ).map((k) => ({ key: k, value: "" }));
        // defaults always first, then any extra keys
        return [
            ...DEFAULT_PARAM_KEYS.map((k) => ({
                key: k,
                value: (existing.find((r) => r.key === k) ?? { value: "" }).value,
            })),
            ...existing.filter((r) => !DEFAULT_PARAM_KEYS.includes(r.key)),
        ];
    });

    useEffect(() => {
        onChange(toObj(rows));
    }, [rows]);

    const setVal = (i, val) =>
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, value: val } : r)));

    const setKey = (i, val) =>
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, key: val } : r)));

    const addRow = () => setRows((p) => [...p, { key: "", value: "" }]);

    const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

    // ── Table header cell style — matches blue header from screenshot ──
    const thSx = {
        bgcolor: "primary.main",
        color: "primary.contrastText",
        fontWeight: 700,
        fontSize: 13,
        py: 1.25,
        px: 2,
        letterSpacing: 0.3,
        borderBottom: "none",
        whiteSpace: "nowrap",
    };

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1, overflow: "hidden" }}
        >
            <Table size="small">
                {/* Header */}
                <TableHead>
                    <TableRow>
                        <TableCell sx={thSx}>Key</TableCell>
                        <TableCell sx={thSx}>Value</TableCell>
                        <TableCell sx={{ ...thSx, width: 130 }} align="center">
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>

                {/* Body */}
                <TableBody>
                    {rows.map((row, i) => {
                        const isDefault = DEFAULT_PARAM_KEYS.includes(row.key);
                        const isEmpty = isDefault && !row.value.trim();

                        return (
                            <TableRow
                                key={i}
                                hover
                                sx={{ "&:last-child td": { border: 0 } }}
                            >
                                {/* Key — locked for defaults, editable for custom */}
                                <TableCell sx={{ px: 2, py: 0.75, width: "35%" }}>
                                    {isDefault ? (
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color="primary.main"
                                            sx={{ textTransform: "capitalize" }}
                                        >
                                            {row.key}
                                        </Typography>
                                    ) : (
                                        <TextField
                                            size="small"
                                            value={row.key}
                                            onChange={(e) => setKey(i, e.target.value)}
                                            disabled={disabled}
                                            placeholder="key"
                                            variant="outlined"
                                            fullWidth
                                            sx={{ "& input": { fontSize: 13 } }}
                                        />
                                    )}
                                </TableCell>

                                {/* Value — always editable */}
                                <TableCell sx={{ px: 2, py: 0.75 }}>
                                    <TextField
                                        size="small"
                                        value={row.value}
                                        onChange={(e) => setVal(i, e.target.value)}
                                        disabled={disabled}
                                        placeholder={`Enter ${row.key || "value"}`}
                                        variant="outlined"
                                        fullWidth
                                        required={isDefault}
                                        error={isEmpty}
                                        helperText={isEmpty ? "Required" : ""}
                                        sx={{ "& input": { fontSize: 13 } }}
                                    />
                                </TableCell>

                                {/* Actions */}
                                <TableCell align="center" sx={{ px: 1.5, py: 0.75, whiteSpace: "nowrap" }}>
                                    {isDefault ? (
                                        // Locked row — no action, just a muted label
                                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                            required
                                        </Typography>
                                    ) : (
                                        // Custom row — red Remove button matching screenshot
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            startIcon={<PlaylistRemove sx={{ fontSize: 16 }} />}
                                            onClick={() => removeRow(i)}
                                            disabled={disabled}
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                textTransform: "none",
                                                px: 1.5,
                                                py: 0.4,
                                                minWidth: 0,
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {/* Add row — green Add button matching screenshot */}
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell colSpan={3} sx={{ px: 2, py: 1, border: 0 }}>
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<PlaylistAdd sx={{ fontSize: 16 }} />}
                                onClick={addRow}
                                disabled={disabled}
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textTransform: "none",
                                    px: 1.5,
                                    py: 0.4,
                                }}
                            >
                                Add
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ─── Shared Project Form Fields ───────────────────────────────────────────────

function ProjectFormFields({ data, onChange, disabled, showDefaultLayers }) {
    const ap = data.additional_params ?? {};

    return (
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <TextField
                fullWidth
                label="Project Name"
                value={data.name ?? ""}
                onChange={(e) => onChange({ ...data, name: e.target.value })}
                disabled={disabled}
                required
                size="small"
                error={!data.name?.trim()}
                helperText={!data.name?.trim() ? "Required" : ""}
            />

            <TextField
                fullWidth
                label="Description"
                multiline
                minRows={2}
                maxRows={4}
                value={data.description ?? ""}
                onChange={(e) => onChange({ ...data, description: e.target.value })}
                disabled={disabled}
                size="small"
            />

            {showDefaultLayers && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={data.default_layers ?? true}
                            onChange={(e) =>
                                onChange({ ...data, default_layers: e.target.checked })
                            }
                            disabled={disabled}
                            size="small"
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body2" color="text.secondary">
                            Add default layers (POP, OFC, Landmark, Crossing…)
                        </Typography>
                    }
                />
            )}

            <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Additional Parameters
                    <Typography
                        component="span"
                        variant="caption"
                        color="error.main"
                        sx={{ ml: 0.5 }}
                    >
                        * state, district, block required
                    </Typography>
                </Typography>
                <AdditionalParamsEditor
                    params={ap}
                    onChange={(updated) =>
                        onChange({ ...data, additional_params: updated })
                    }
                    disabled={disabled}
                />
            </Box>
        </Stack>
    );
}

// ─── Row Action Menu (Edit / Delete) ─────────────────────────────────────────

function RowActionMenu({ project, onEdit, onDelete }) {
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);

    return (
        <>
            <Tooltip title="Actions">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setAnchor(e.currentTarget);
                    }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{
                    paper: { elevation: 3, sx: { minWidth: 140, borderRadius: 2 } },
                }}
            >
                <MenuItem
                    dense
                    onClick={() => { onEdit(project); setAnchor(null); }}
                >
                    <ListItemIcon><Edit fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Edit</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    dense
                    onClick={() => { onDelete(project.id); setAnchor(null); }}
                    sx={{ color: "error.main" }}
                >
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProjectsPage({ page_state, project_state }) {
    const [projectToBeLounched, setProjectToBeLounched] = project_state;
    const [page, setPage] = page_state;

    const [projects, setProjects] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [newProject, setNewProject] = useState(EMPTY_NEW);

    const [editOpen, setEditOpen] = useState(false);
    const [editProject, setEditProject] = useState({
        id: null, name: "", description: "",
        additional_params: { state: "", district: "", block: "" },
    });

    const [deleteId, setDeleteId] = useState(null);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const isFormValid = (data) => {
        const ap = data.additional_params ?? {};
        return (
            (data.name ?? "").trim() !== "" &&
            DEFAULT_PARAM_KEYS.every((k) => (ap[k] ?? "").trim() !== "")
        );
    };

    // ── Fetch ──────────────────────────────────────────────────────────────
    const fetchProjects = async (pageNum) => {
        setLoading(true);
        try {
            const res = await fetch(`${project_api_path}/?page=${pageNum}&page_size=${pageSize}`);
            if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
            const data = await res.json();
            setProjects(data.items);
            setTotalPages(Math.ceil(data.total / pageSize));
            showSnackbar(`Loaded ${data.total} projects`, "success");
        } catch (err) {
            showSnackbar(`Error loading projects: ${err}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProjects(page); }, [page]); // eslint-disable-line

    // ── Create ─────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        setActionLoading(true);
        try {
            const res = await fetch(`${project_api_path}/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newProject),
            });
            if (!res.ok) throw new Error(`Create failed: ${res.status}`);
            await fetchProjects(page);
            setCreateOpen(false);
            setNewProject(EMPTY_NEW);
            showSnackbar(`Project "${newProject.name}" created.`, "success");
        } catch (err) {
            showSnackbar(`${err}`, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Edit ───────────────────────────────────────────────────────────────
    const handleEdit = async () => {
        setActionLoading(true);
        try {
            const res = await fetch(`${project_api_path}/${editProject.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editProject),
            });
            if (!res.ok) throw new Error(await res.text());
            const updated = await res.json();
            setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            setEditOpen(false);
            showSnackbar(`Project "${updated.name}" updated.`, "success");
        } catch (err) {
            showSnackbar(err.message, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setActionLoading(true);
        try {
            const res = await fetch(`${project_api_path}/${deleteId}`, { method: "DELETE" });
            if (!res.ok) throw new Error(await res.text());
            setProjects((prev) => prev.filter((p) => p.id !== deleteId));
            setDeleteId(null);
            showSnackbar("Project deleted.", "success");
        } catch (err) {
            showSnackbar(err.message, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Loading ────────────────────────────────────────────────────────────
    if (loading)
        return (
            <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2 }}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">Loading projects…</Typography>
            </Box>
        );

    const hasActiveFilters = Object.values(filters).some((v) => v.trim());

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "grey.100" }}>

            {/* ── AppBar ── */}
            <AppBar position="static" color="primary" elevation={2}>
                <Toolbar variant="dense" sx={{ gap: 1.5 }}>
                    <MapOutlined />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                            BSNL Network Planner
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            Fiber Route &amp; Ring Management
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Container maxWidth="xl" sx={{ flex: 1, display: "flex", flexDirection: "column", py: 3, gap: 2, overflow: "hidden" }}>

                {/* Title row */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" fontWeight={700}>Projects</Typography>
                    <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
                        New Project
                    </Button>
                </Box>

                {/* ── Filter Bar ── */}
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <FilterAlt fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mr: 0.5 }}>
                            FILTER
                        </Typography>

                        {DEFAULT_PARAM_KEYS.map((key) => (
                            <TextField
                                key={key}
                                size="small"
                                label={key.charAt(0).toUpperCase() + key.slice(1)}
                                value={filters[key]}
                                onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                                sx={{ width: 160 }}
                                slotProps={{
                                    input: {
                                        endAdornment: filters[key] ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" edge="end" onClick={() => setFilters((f) => ({ ...f, [key]: "" }))}>
                                                    <Remove sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }
                                }}
                            />
                        ))}

                        {hasActiveFilters && (
                            <Button size="small" color="inherit" startIcon={<SearchOff fontSize="small" />} onClick={() => setFilters(EMPTY_FILTERS)} sx={{ color: "text.secondary", fontSize: 12 }}>
                                Clear all
                            </Button>
                        )}

                        <Typography variant="caption" color="text.disabled" sx={{ ml: "auto", fontStyle: "italic" }}>
                            Filters not wired — UI only
                        </Typography>
                    </Box>
                </Paper>

                {/* ── Table ── */}
                <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflowY: "auto", borderRadius: 2 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {["#", "Project Name", "Description", "State", "District", "Block", "Launch", "Actions"].map((col, i) => (
                                    <TableCell
                                        key={col}
                                        align={i >= 6 ? "center" : "left"}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: "primary.contrastText",
                                            bgcolor: "primary.main",
                                            borderBottom: "none",
                                            whiteSpace: "nowrap",
                                            py: 1.25,
                                            letterSpacing: 0.3,
                                        }}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {projects.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                        <Typography color="text.disabled" variant="body2">
                                            No projects found. Create one to get started.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                projects.map((project, index) => {
                                    const ap = project.additional_params ?? {};
                                    return (
                                        <TableRow key={project.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                            <TableCell sx={{ color: "text.disabled", fontSize: 12, width: 40 }}>
                                                {(page - 1) * pageSize + index + 1}
                                            </TableCell>

                                            <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                                {project.name}
                                            </TableCell>

                                            <TableCell sx={{ color: "text.secondary", fontSize: 13, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {project.description || (
                                                    <Typography component="span" variant="caption" color="text.disabled" fontStyle="italic">—</Typography>
                                                )}
                                            </TableCell>

                                            {["state", "district", "block"].map((k) => (
                                                <TableCell key={k}>
                                                    {ap[k] ? (
                                                        <Chip label={ap[k]} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                                    )}
                                                </TableCell>
                                            ))}

                                            <TableCell align="center">
                                                <Tooltip title="Open in Route Planner">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setLoading(true);
                                                            setProjectToBeLounched(project);
                                                            setTimeout(() => { navigate("/route-planner"); setLoading(false); }, 100);
                                                        }}
                                                    >
                                                        <Launch fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell align="center">
                                                <RowActionMenu
                                                    project={project}
                                                    onEdit={(p) => {
                                                        setEditProject({
                                                            ...p,
                                                            additional_params: p.additional_params ?? { state: "", district: "", block: "" },
                                                        });
                                                        setEditOpen(true);
                                                    }}
                                                    onDelete={setDeleteId}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" size="small" />
                </Box>
            </Container>

            {/* ── Footer ── */}
            <Box component="footer" sx={{ px: 3, py: 1, bgcolor: "primary.dark", color: "primary.contrastText", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>BSNL Network Planner — Internal Tool</Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>© {new Date().getFullYear()}</Typography>
            </Box>

            {/* ── Create Dialog ── */}
            <Dialog open={createOpen} onClose={() => !actionLoading && setCreateOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus disableEnforceFocus keepMounted>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Add color="primary" fontSize="small" />
                    Create New Project
                </DialogTitle>
                <DialogContent sx={{ pt: "20px !important" }}>
                    <ProjectFormFields data={newProject} onChange={setNewProject} disabled={actionLoading} showDefaultLayers />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                    <Button onClick={() => { setCreateOpen(false); setNewProject(EMPTY_NEW); }} disabled={actionLoading} color="inherit">Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleCreate} disabled={actionLoading || !isFormValid(newProject)}
                        startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <Add />}>
                        {actionLoading ? "Creating…" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Dialog ── */}
            <Dialog open={editOpen} onClose={() => !actionLoading && setEditOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus disableEnforceFocus keepMounted>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Edit color="primary" fontSize="small" />
                    Edit Project
                    {editProject.id && (
                        <Typography variant="caption" color="text.disabled" sx={{ ml: "auto", fontWeight: 400 }}>ID: {editProject.id}</Typography>
                    )}
                </DialogTitle>
                <DialogContent sx={{ pt: "20px !important" }}>
                    <ProjectFormFields data={editProject} onChange={setEditProject} disabled={actionLoading} showDefaultLayers={false} />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                    <Button onClick={() => setEditOpen(false)} disabled={actionLoading} color="inherit">Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleEdit} disabled={actionLoading || !isFormValid(editProject)}
                        startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <Edit />}>
                        {actionLoading ? "Saving…" : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Dialog ── */}
            <Dialog open={Boolean(deleteId)} onClose={() => !actionLoading && setDeleteId(null)} maxWidth="xs" fullWidth>
                <DialogTitle fontWeight={700}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This will permanently delete the project and all associated data. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setDeleteId(null)} disabled={actionLoading} color="inherit">Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <Delete />}>
                        {actionLoading ? "Deleting…" : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}